import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
  Chip,
  Stack,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import { alpha, useTheme, Theme } from '@mui/material/styles';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { AlertDashboardNotice, AlertSlotMonitorRow } from '../types/dashboard';
import { alertKindDisplayLabel } from '../utils/alertUserFacingText';
import {
  deriveTradeState,
  slotVisual,
  SlotTone,
  TradeStateKey,
} from '../utils/dashboardTradeState';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';

interface AlertSlotMonitorProps {
  rows: AlertSlotMonitorRow[];
  /** Notices for alerts not shown in the slot monitor (e.g. single-trigger). */
  extraNotices?: AlertDashboardNotice[];
  loading?: boolean;
  /** Called after a successful reset so the dashboard can refresh slot state. */
  onResetComplete?: () => void;
}

function stateColor(theme: Theme, key: TradeStateKey): string {
  switch (key) {
    case 'in-long':
      return theme.palette.success.main;
    case 'in-short':
      return theme.palette.error.main;
    case 'flat':
      return theme.palette.warning.main;
    default:
      return theme.palette.text.disabled;
  }
}

function toneColor(theme: Theme, tone: SlotTone): string {
  if (tone === 'up') return theme.palette.success.main;
  if (tone === 'down') return theme.palette.error.main;
  return theme.palette.text.disabled;
}

function fmtTime(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
}

const AlertSlotMonitor: React.FC<AlertSlotMonitorProps> = ({
  rows,
  extraNotices = [],
  loading = false,
  onResetComplete,
}) => {
  const theme = useTheme();
  const { token } = useAuth();
  const [resettingId, setResettingId] = useState<number | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  const handleReset = async (row: AlertSlotMonitorRow) => {
    if (!token || resettingId != null) return;
    setResettingId(row.alertId);
    setResetError(null);
    try {
      const res =
        row.alertKind === 'bidirectional_multi'
          ? await apiService.resetBidirectionalSlots(row.alertId, {}, token)
          : await apiService.resetDirectionalSlots(row.alertId, {}, token);
      if (!res.success) {
        setResetError(res.error || 'Reset failed');
      } else {
        onResetComplete?.();
      }
    } catch (e) {
      setResetError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setResettingId(null);
    }
  };

  const rowAlertIds = new Set(rows.map((r) => r.alertId));
  const standaloneNotices = extraNotices.filter((n) => !rowAlertIds.has(n.alertId));

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Live trades
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Loading slot states…
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!rows.length && !standaloneNotices.length) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Live trades
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No active Directional Trade or Two-Way Trade alerts. Create one on the Alerts tab to see
            indicator status here.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const renderNotice = (notice: AlertDashboardNotice) => (
    <Box
      key={notice.alertId}
      sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
    >
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {notice.stockSymbol} · {notice.positionType}
      </Typography>
      <Alert severity={notice.severity} variant="outlined">
        {notice.message}
      </Alert>
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Live trades
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '66ch' }}>
          When your indicators align, an entry trade is sent (long or short). When any part of that
          alignment breaks, an exit trade is sent. When they align again, a new entry is sent — so
          each alert moves in and out of a position as your indicators change. “Reset to neutral” is
          a manual override you’ll rarely need.
        </Typography>

        {/* Legend */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2.25,
            px: 1.75,
            py: 1.25,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: alpha(theme.palette.text.primary, 0.02),
            fontSize: 12,
            color: 'text.secondary',
          }}
        >
          {[
            { c: theme.palette.success.main, t: 'Bullish' },
            { c: theme.palette.error.main, t: 'Bearish' },
            { c: theme.palette.text.disabled, t: 'Neutral / waiting' },
          ].map((lg) => (
            <Box key={lg.t} sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: lg.c }} />
              <Typography variant="caption" color="text.secondary">
                {lg.t}
              </Typography>
            </Box>
          ))}
        </Box>

        {resetError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setResetError(null)}>
            {resetError}
          </Alert>
        )}

        <Stack spacing={1.5}>
          {rows.map((row) => {
            const state = deriveTradeState(row);
            const accent = stateColor(theme, state.key);
            const alertName = row.alertName?.trim();
            const secondaryLabel = (alertName ? [row.stockSymbol, row.positionType] : [row.positionType])
              .filter(Boolean)
              .join(' · ');
            const lastFired = fmtTime(row.lastSuccessAt);
            const lastAttempt = fmtTime(row.lastAttemptAt ?? row.lastTriggeredAt);
            const attemptStatus = (row.lastAttemptStatus ?? '').toLowerCase();
            const isResetting = resettingId === row.alertId;

            return (
              <Box
                key={row.alertId}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  p: 2,
                  pl: 2.25,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: alpha(theme.palette.text.primary, 0.02),
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    backgroundColor: accent,
                  },
                }}
              >
                {/* Header row */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 700, letterSpacing: '0.01em' }}>
                    {alertName || row.stockSymbol}
                  </Typography>
                  {secondaryLabel && (
                    <Typography variant="body2" color="text.secondary">
                      · {secondaryLabel}
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    label={alertKindDisplayLabel(row.alertKind)}
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                  <Box flexGrow={1} />
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.9,
                      px: 1.25,
                      py: 0.4,
                      borderRadius: 5,
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: accent,
                      border: '1px solid',
                      borderColor: alpha(accent, 0.35),
                      backgroundColor: alpha(accent, 0.12),
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: accent,
                        boxShadow:
                          state.key === 'idle' ? 'none' : `0 0 7px ${alpha(accent, 0.8)}`,
                      }}
                    />
                    {state.label}
                  </Box>
                </Box>

                {/* Indicator dots */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 1.5 }}>
                  {row.slots.map((_, i) => {
                    const v = slotVisual(row, i);
                    const source = row.slotSources[i] ?? '?';
                    const dot = toneColor(theme, v.tone);
                    return (
                      <Tooltip key={i} title={`${v.name} (${source}): ${v.stateText}`}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'background.paper',
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              flexShrink: 0,
                              backgroundColor: dot,
                              boxShadow:
                                v.tone === 'neutral' ? 'none' : `0 0 7px ${alpha(dot, 0.6)}`,
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: 12.5, color: 'text.primary' }}>
                            {v.name}
                          </Typography>
                          <Typography
                            variant="overline"
                            sx={{ fontSize: 9.5, lineHeight: 1, color: 'text.secondary' }}
                          >
                            {v.stateText}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>

                {row.incomingWebhookNotice && (
                  <Alert
                    severity={row.incomingWebhookSeverity ?? 'warning'}
                    sx={{ mb: 1.5 }}
                    variant="outlined"
                  >
                    {row.incomingWebhookNotice}
                  </Alert>
                )}

                {/* Footer */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
                    <Typography variant="caption" color="text.secondary">
                      Last action
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: lastFired ? 'success.main' : 'text.secondary' }}
                    >
                      {lastFired ? `Webhook fired · ${lastFired}` : 'None yet'}
                    </Typography>
                  </Box>
                  {lastAttempt && attemptStatus && attemptStatus !== 'success' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
                      <Typography variant="caption" color="text.secondary">
                        Last signal
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: attemptStatus === 'error' ? 'error.main' : 'text.secondary' }}
                      >
                        {lastAttempt}
                        {attemptStatus === 'pending' ? ' · waiting for alignment' : ` · ${attemptStatus}`}
                      </Typography>
                    </Box>
                  )}
                  <Box flexGrow={1} />
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={isResetting ? <CircularProgress size={14} /> : <RestartAltIcon />}
                    disabled={isResetting || !token}
                    onClick={() => handleReset(row)}
                  >
                    Reset to neutral
                  </Button>
                </Box>
              </Box>
            );
          })}
          {standaloneNotices.map(renderNotice)}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AlertSlotMonitor;
